import { NodeType } from "@features/builder/graph/types"
import { JSONSchema7 } from "json-schema"

export type DashboardNode = {
  label: string
  type: NodeType
}

export function splitNodesByType(nodes: DashboardNode[]) {
  const defaultNodesNames: string[] = []
  const categoricalNodesNames: string[] = []
  const tableNodesNames: string[] = []

  for (const node of nodes) {
    if (node.type === NodeType.Categorical) {
      categoricalNodesNames.push(node.label)
    } else if (node.type === NodeType.Table) {
      tableNodesNames.push(node.label)
    } else {
      defaultNodesNames.push(node.label)
    }
  }

  return { defaultNodesNames, categoricalNodesNames, tableNodesNames }
}

function nodes(nodesNames: string[]): JSONSchema7 {
  return {
    type: "array",
    minItems: 1,
    items: {
      enum: nodesNames,
    } satisfies JSONSchema7,
    uniqueItems: true,
    description: "List of node names",
  }
}

const showStandardDeviations: JSONSchema7 = {
  type: "boolean",
  description: "works only if `Aggregate` is set to `mean` - defaults to `false` (optional)",
}

const chartTitle: JSONSchema7 = {
  type: "string",
  description: "Title of chart",
}

const chartDescription: JSONSchema7 = {
  type: "object",
  properties: {
    Title: { type: "string" },
    Content: {
      type: "array",
      minItems: 1,
      items: { type: "string" },
    },
  },
  required: ["Title", "Content"],
  additionalProperties: false,
}

const commonChartProperties: Record<string, JSONSchema7> = {
  Title: chartTitle,
  Description: chartDescription,
  Section: {
    type: "string",
    description: "Section name for grouping charts",
  },
  FillColumns: {
    type: "string",
    description: "Chart width in layout (e.g. '50-100')",
  },
  FillRows: {
    type: "string",
    description: "Chart height in layout (e.g. '0-100')",
  },
  LegendPosition: {
    enum: ["top", "bottom"],
    description: "Legend position - defaults to top",
  },
}

const aggregateOptionsSchema: JSONSchema7 = {
  type: "array",
  minItems: 1,
  items: {
    type: "string",
    enum: [
      "mean",
      "std",
      "median",
      "max",
      "min",
      "5th_quantile",
      "10th_quantile",
      "25th_quantile",
      "50th_quantile",
      "75th_quantile",
      "90th_quantile",
      "95th_quantile",
    ],
  },
}

const layout: JSONSchema7 = {
  type: "array",
  minItems: 1,
  items: {
    type: "object",
    properties: {
      Height: {
        type: "number",
        description: "Height of grid area in vh units - defaults to 100",
      },
      Section: {
        type: "string",
        description: "Section name this layout applies to - omit for charts without section",
      },
      NumberOfColumns: {
        type: "number",
        description: "Number of equally sized columns - defaults to 100",
      },
      NumberOfRows: {
        type: "number",
        description: "Number of equally sized rows - defaults to 100",
      },
    },
  },
  description: "Grid layout definitions for chart sections",
}

function nodeSelect({
  nodesNames,
  optionSchema = {},
}: {
  nodesNames: string[]
  optionSchema?: JSONSchema7
}): JSONSchema7 {
  return {
    type: "array",
    minItems: 1,
    items: {
      type: "object",
      ...optionSchema,
      additionalProperties: false,
      required: ["Option", "Nodes", ...(optionSchema.required ?? [])],
      properties: {
        Option: {
          type: "string",
          description: "Label that will be shown in the dropdown",
        },
        Nodes: nodes(nodesNames),
        ...(optionSchema?.properties ?? {}),
      },
    },
    description: "Optional - define multiple charts in single box with dropdown",
  }
}

function timeSeriesChart({ nodesNames }: { nodesNames: string[] }): JSONSchema7 {
  return {
    type: "object",
    properties: {
      Type: { enum: ["TimeSeries"] },
      ...commonChartProperties,
      Nodes: nodes(nodesNames),
      NodeSelect: nodeSelect({ nodesNames }),
      Aggregate: {
        type: "string",
        enum: ["mean", "median"],
        description: "Aggregate list for this chart",
      },
      ShowStandardDeviations: showStandardDeviations,
      ShowAsTable: { type: "boolean" },
    },
    required: ["Type", "Title"],
    additionalProperties: false,
    description: "Time series chart can be defined for a single node",
  }
}

function barChart({ nodesNames }: { nodesNames: string[] }): JSONSchema7 {
  const compareSchema: JSONSchema7 = {
    type: "string",
    // @TODO (Bartek Laskowski) 2025-10-22: to make them fully dynamic we need to use updateSchema() with selected list of nodes when we are in this field or when node was added
    enum: nodesNames,
    description: "Compare node must be pressent in corresponding Nodes section",
  }

  return {
    type: "object",
    properties: {
      Type: { enum: ["BarChart"] },
      ...commonChartProperties,
      Nodes: nodes(nodesNames),
      NodeSelect: nodeSelect({
        nodesNames,
        optionSchema: {
          properties: {
            Compare: compareSchema,
            Stacked: { type: "boolean" },
            ShowAsPercentage: { type: "boolean" },
          },
        },
      }),
      Aggregate: {
        type: "string",
        enum: ["mean", "median"],
        description: "Aggregate list for this chart",
      },
      ShowStandardDeviations: showStandardDeviations,
      ShowAsTable: { type: "boolean" },
      ShowAsPercentage: {
        type: "boolean",
        description:
          "If `true`, values are displayed as percentages; if `false`, values are shown as raw values - defaults to `false`",
      },
      Stacked: {
        type: "boolean",
        description:
          "Bar stacking mode. `true` renders stacked bars, `false` renders grouped bars - defaults to `true`",
      },
      Compare: compareSchema,
    },
    required: ["Type", "Title"],
    additionalProperties: false,
    description: "Bar chart is multi node chart",
  }
}

function compareResultsBarChart({ nodesNames }: { nodesNames: string[] }): JSONSchema7 {
  return {
    type: "object",
    properties: {
      Type: { enum: ["BarChart"] },
      ...commonChartProperties,
      Nodes: nodes(nodesNames),
      NodeSelect: nodeSelect({ nodesNames }),
      Aggregate: {
        type: "string",
        enum: ["mean", "median"],
        description: "Aggregate list for this chart",
      },
      ShowStandardDeviations: showStandardDeviations,
      ShowAsTable: { type: "boolean" },
    },
    required: ["Type", "Title"],
    additionalProperties: false,
    description: "Bar chart is multi node chart",
  }
}

function barCategoricalChart({ nodesNames }: { nodesNames: string[] }): JSONSchema7 {
  return {
    type: "object",
    properties: {
      Type: { enum: ["BarCategoricalChart"] },
      ...commonChartProperties,
      Nodes: nodes(nodesNames),
      NodeSelect: nodeSelect({ nodesNames }),
      ShowAsTable: { type: "boolean" },
      Stacked: {
        type: "boolean",
        description:
          "Bar stacking mode. `true` renders stacked bars, `false` renders grouped bars - defaults to `true`",
      },
      ShowAsPercentage: {
        type: "boolean",
        description:
          "If `true`, values are displayed as percentages; if `false`, values are shown as sample counts - defaults to `true`",
      },
    },
    required: ["Type", "Title"],
    additionalProperties: false,
    description: "Categorical bar chart",
  }
}

function compareResultsBarCategoricalChart({ nodesNames }: { nodesNames: string[] }): JSONSchema7 {
  return {
    type: "object",
    properties: {
      Type: { enum: ["BarCategoricalChart"] },
      ...commonChartProperties,
      Nodes: nodes(nodesNames),
      NodeSelect: nodeSelect({ nodesNames }),
      ShowAsTable: { type: "boolean" },
    },
    required: ["Type", "Title"],
    additionalProperties: false,
    description: "Categorical bar chart",
  }
}

function scatterPlotChart({ nodesNames }: { nodesNames: string[] }): JSONSchema7 {
  return {
    type: "object",
    properties: {
      Type: { enum: ["ScatterPlot"] },
      ...commonChartProperties,
      Nodes: nodes(nodesNames),
      NodeSelect: nodeSelect({ nodesNames }),
    },
    required: ["Type", "Title"],
    additionalProperties: false,
    description: "ScatterPlot chart is single node chart",
  }
}

function probabilityDensityChart({ nodesNames }: { nodesNames: string[] }): JSONSchema7 {
  return {
    type: "object",
    properties: {
      Type: { enum: ["ProbabilityDensity"] },
      ...commonChartProperties,
      Nodes: nodes(nodesNames),
      NodeSelect: nodeSelect({ nodesNames }),
    },
    required: ["Type", "Title"],
    additionalProperties: false,
    description: "Probability density chart is single node chart",
  }
}

function thresholdChart({ nodesNames }: { nodesNames: string[] }): JSONSchema7 {
  return {
    type: "object",
    properties: {
      Type: { enum: ["Threshold"] },
      ...commonChartProperties,
      Nodes: nodes(nodesNames),
      NodeSelect: nodeSelect({ nodesNames }),
      Default_given_event_node: {
        type: "string",
        description: "defaults to first node of Nodes list (optional)",
      },
      Default_event_node: {
        type: "string",
        description: "defaults to second node of Nodes list (optional)",
      },
    },
    required: ["Type", "Title"],
    additionalProperties: false,
    description:
      "Threshold chart is to check different scenarios comparing results for 2 nodes.\nMust contain at least 2 nodes.",
  }
}

function aggregateChart({ nodesNames }: { nodesNames: string[] }): JSONSchema7 {
  return {
    type: "object",
    properties: {
      Type: { enum: ["Aggregate"] },
      ...commonChartProperties,
      Nodes: nodes(nodesNames),
      NodeSelect: nodeSelect({
        nodesNames,
        optionSchema: {
          properties: { Aggregate: aggregateOptionsSchema },
          required: ["Aggregate"],
        },
      }),
      Aggregate: aggregateOptionsSchema,
    },
    required: ["Type", "Title"],
    additionalProperties: false,
    description: "Aggregate node chart with entry for each Aggregate from the list",
  }
}
function tableChart({ nodesNames }: { nodesNames: string[] }): JSONSchema7 {
  return {
    type: "object",
    properties: {
      Type: { enum: ["Table"] },
      ...commonChartProperties,
      Nodes: nodes(nodesNames),
      NodeSelect: nodeSelect({ nodesNames }),
    },
    required: ["Type", "Title"],
    additionalProperties: false,
    description:
      "Table is not really a chart, but you can use is to show node content of Table nodes",
  }
}

function createCharts({
  chartSchemaMap,
}: {
  chartSchemaMap: Record<string, JSONSchema7>
}): JSONSchema7 {
  const chartTypes = Object.keys(chartSchemaMap)

  return {
    type: "array",
    minItems: 1,
    items: {
      type: "object",
      properties: {
        Type: {
          type: "string",
          enum: chartTypes,
        },
      },
      required: ["Type"],
      allOf: Object.entries(chartSchemaMap).map(([type, schema]) => ({
        if: {
          properties: {
            Type: {
              const: type,
            },
          },
          required: ["Type"],
        },
        then: schema,
      })),
    } as JSONSchema7,
    additionalProperties: false,
    description: "Array of charts in this group",
  }
}

function charts(nodes: DashboardNode[]): JSONSchema7 {
  const { defaultNodesNames, categoricalNodesNames, tableNodesNames } = splitNodesByType(nodes)

  return createCharts({
    chartSchemaMap: {
      TimeSeries: timeSeriesChart({ nodesNames: defaultNodesNames }),
      BarChart: barChart({ nodesNames: defaultNodesNames }),
      BarCategoricalChart: barCategoricalChart({ nodesNames: categoricalNodesNames }),
      ScatterPlot: scatterPlotChart({ nodesNames: defaultNodesNames }),
      ProbabilityDensity: probabilityDensityChart({ nodesNames: defaultNodesNames }),
      Aggregate: aggregateChart({ nodesNames: defaultNodesNames }),
      Table: tableChart({ nodesNames: tableNodesNames }),
      Threshold: thresholdChart({ nodesNames: defaultNodesNames }),
    },
  })
}

function compareResultsCharts(nodes: DashboardNode[]): JSONSchema7 {
  const { defaultNodesNames, categoricalNodesNames, tableNodesNames } = splitNodesByType(nodes)

  return createCharts({
    chartSchemaMap: {
      TimeSeries: timeSeriesChart({ nodesNames: defaultNodesNames }),
      BarChart: compareResultsBarChart({ nodesNames: defaultNodesNames }),
      BarCategoricalChart: compareResultsBarCategoricalChart({
        nodesNames: categoricalNodesNames,
      }),
      ScatterPlot: scatterPlotChart({ nodesNames: defaultNodesNames }),
      ProbabilityDensity: probabilityDensityChart({ nodesNames: defaultNodesNames }),
      Aggregate: aggregateChart({ nodesNames: defaultNodesNames }),
      Table: tableChart({ nodesNames: tableNodesNames }),
    },
  })
}

function Groups({ chartsSchema }: { chartsSchema: JSONSchema7 }): JSONSchema7 {
  return {
    type: "array",
    minItems: 1,
    items: {
      type: "object",
      properties: {
        Charts: chartsSchema,
        Name: {
          type: "string",
          description: "Group name",
        },
        Layout: layout,
      },
      required: ["Name", "Charts"],
    },
    description: "Array of dashboard groups",
  }
}

export function createYamlDashboardSchema(nodes: DashboardNode[] = []): JSONSchema7 {
  return {
    type: "object",
    properties: {
      version: {
        type: "number",
        description: "Configuration version number",
      },
      Title: {
        type: "string",
        description: "Dashboard title",
      },
      Groups: Groups({
        chartsSchema: charts(nodes),
      }),
      CompareResults: {
        type: "object",
        properties: {
          Groups: Groups({
            chartsSchema: compareResultsCharts(nodes),
          }),
        },
        required: ["Groups"],
        additionalProperties: false,
      },
    },
    required: ["Groups", "Title", "version"],
    additionalProperties: false,
  }
}
